import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
} from '@mui/material';
import UserCard from './UserCard';
import ImagePreview from './ImagePreview';
import { User } from '../../types/interfaces';
import { useGetAllUsersQuery, useGetAllLocationsQuery } from '../../redux/adminApiSlice';

const getProfilePicture = (url?: string) => {
  if (!url) return undefined;
  if (url.startsWith('http')) {
    return url;
  }
  if (url.startsWith('file://')) {
    return undefined;
  }
  return `http://localhost:3000/${url}`;
};

const UsersList: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { 
    data: users = [], 
    isLoading: isLoadingUsers, 
    error: usersError,
    refetch: refetchUsers
  } = useGetAllUsersQuery();

  const { 
    data: locations = [], 
    isLoading: isLoadingLocations, 
    error: locationsError 
  } = useGetAllLocationsQuery();

  // Sync selected user with updated users list when users data changes
  useEffect(() => {
    if (selectedUser && users.length > 0) {
      const updatedSelectedUser = users.find(user => user._id === selectedUser._id);
      if (updatedSelectedUser) {
        setSelectedUser(updatedSelectedUser);
      }
    }
  }, [users, selectedUser]); // Only depend on users array to trigger when cache updates

  const handleUserUpdate = (updatedUser: User | null) => {
    if (updatedUser === null) {
      // User was deleted, remove from list
      setSelectedUser(null);
      refetchUsers(); // Refresh the users list
    } else {
      // User was updated, update selected user immediately
      setSelectedUser(updatedUser);
      // Note: RTK Query will automatically update the cache due to invalidatesTags: ['Users']
      // No need to manually refetch as it will happen automatically
    }
  };

  const handleUserDeleted = (userId: string) => {
    setSelectedUser(null);
    refetchUsers(); // Refresh the users list
  };

  const handleAvatarClick = (e: React.MouseEvent, profilePicture?: string) => {
    e.stopPropagation(); // Prevent triggering the ListItem click
    if (profilePicture) {
      const fullUrl = getProfilePicture(profilePicture);
      if (fullUrl) {
        setPreviewImage(fullUrl);
      }
    }
  };

  if (isLoadingUsers || isLoadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (usersError || locationsError) {
    return (
      <Box p={3}>
        <Typography color="error">
          {usersError ? 'Failed to load users' : 'Failed to load locations'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100%',
    }}>
      {/* Image Preview Modal */}
      <ImagePreview
        open={!!previewImage}
        imageUrl={previewImage || ''}
        onClose={() => setPreviewImage(null)}
      />

      {/* Sidebar */}
      <Box sx={{ 
        width: 300, 
        minWidth: 300, 
        flexShrink: 0, 
        borderRight: 1, 
        borderColor: 'divider',
        overflow: 'auto',
        bgcolor: 'background.paper',
      }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Users ({users.length})</Typography>
          <Typography variant="caption" color="text.secondary">
            {users.filter(u => u.isMockUser).length} mock users
          </Typography>
        </Box>
        <List>
          {users.map(user => (
            <ListItem 
              key={user._id} 
              button 
              onClick={() => {
                setSelectedUser(user);
              }}
              selected={selectedUser?._id === user._id}
            >
              <ListItemAvatar>
                <Avatar 
                  src={getProfilePicture(user.facePicture)}
                  onClick={(e) => handleAvatarClick(e, user.facePicture)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }}
                />
              </ListItemAvatar>
              <ListItemText 
                primary={user.displayName}
                secondary={`@${user.username}`}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        bgcolor: 'grey.50',
      }}>
        <Box sx={{ p: 3 }}>
          {selectedUser ? (
            <>
              <Typography variant="h5" gutterBottom>User Details</Typography>
              <UserCard 
                user={selectedUser} 
                locations={locations} 
                onUserUpdate={handleUserUpdate}
                onUserDeleted={handleUserDeleted}
              />
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
              Select a user to view details
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UsersList; 